import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { Sun, Moon, LogOut, User, Menu, ChevronDown, Coins } from 'lucide-react';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import AnimatedLogo from '../common/AnimatedLogo';
import api from '../../utils/api';

const Header = ({ onMenuClick }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [showDropdown, setShowDropdown] = useState(false);
  const [credits, setCredits] = useState(null);
  const dropdownRef = useRef(null);

  // Fetch user credits
  useEffect(() => {
    const fetchCredits = async () => {
      if (user) {
        try {
          const response = await api.get('/api/subscription/credits');
          setCredits(response.data.credits);
        } catch (error) {
          console.error('Failed to fetch credits:', error);
        }
      }
    };
    fetchCredits();
    
    // Refresh credits every 30 seconds
    const interval = setInterval(fetchCredits, 30000);
    return () => clearInterval(interval);
  }, [user]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="h-14 border-b border-zinc-800 bg-zinc-900/80 backdrop-blur-sm flex items-center justify-between px-4">
      <div className="flex items-center gap-3">
        {/* Mobile menu button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onMenuClick}
          className="h-9 w-9 text-zinc-400 hover:text-white hover:bg-zinc-800 lg:hidden"
        >
          <Menu className="h-5 w-5" />
        </Button>
        <AnimatedLogo size="sm" />
        <span className="text-sm font-medium text-zinc-400 hidden sm:inline">AI Studio</span>
      </div>

      <div className="flex items-center gap-2 sm:gap-3">
        {/* Credits Display */}
        {user && credits !== null && (
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 border border-amber-500/30 rounded-full">
            <Coins className="h-4 w-4 text-amber-400" />
            <span className="text-sm font-semibold text-amber-400">{credits?.toLocaleString() || 0}</span>
            <span className="text-xs text-amber-400/70 hidden sm:inline">credits</span>
          </div>
        )}
        
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          className="h-9 w-9 text-zinc-400 hover:text-white hover:bg-zinc-800"
        >
          {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </Button>

        {user && (
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className="flex items-center gap-2 h-9 px-2 rounded-md hover:bg-zinc-800 transition-colors"
            >
              <Avatar className="h-7 w-7">
                <AvatarImage src={user.picture} alt={user.name} />
                <AvatarFallback className="bg-zinc-700 text-white text-xs">
                  {user.name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              <span className="text-sm text-zinc-300 hidden sm:inline">{user.name}</span>
              <ChevronDown className="h-4 w-4 text-zinc-400 hidden sm:block" />
            </button>
            
            {showDropdown && (
              <div className="absolute right-0 top-full mt-1 w-56 bg-zinc-900 border border-zinc-800 rounded-md shadow-lg z-50 py-1">
                <div className="px-3 py-2 border-b border-zinc-800">
                  <p className="text-sm font-medium text-white">{user.name}</p>
                  <p className="text-xs text-zinc-400 truncate">{user.email}</p>
                  {credits !== null && (
                    <div className="flex items-center gap-1 mt-1">
                      <Coins className="h-3 w-3 text-amber-400" />
                      <span className="text-xs text-amber-400">{credits?.toLocaleString() || 0} credits</span>
                    </div>
                  )}
                </div>
                <button
                  className="w-full px-3 py-2 text-left text-sm text-zinc-300 hover:bg-zinc-800 flex items-center gap-2"
                  onClick={() => setShowDropdown(false)}
                >
                  <User className="h-4 w-4" />
                  Profile
                </button>
                <div className="border-t border-zinc-800 mt-1 pt-1">
                  <button
                    onClick={() => {
                      setShowDropdown(false);
                      logout();
                    }}
                    className="w-full px-3 py-2 text-left text-sm text-red-400 hover:bg-red-400/10 flex items-center gap-2"
                  >
                    <LogOut className="h-4 w-4" />
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
